const HEX6 = /^#?([0-9a-f]{6})$/i;
const HEX3 = /^#?([0-9a-f]{3})$/i;

/** Normalizes user input to `#RRGGBB` or returns null when invalid. */
export function normalizeHexColor(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  const six = HEX6.exec(withHash);
  if (six) return `#${six[1]!.toUpperCase()}`;
  const three = HEX3.exec(withHash);
  if (three) {
    const [r, g, b] = three[1]!.split("");
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return null;
}

function channelToLinear(channel: number): number {
  const s = channel / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function parseRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return null;
  const raw = normalized.slice(1);
  return {
    r: Number.parseInt(raw.slice(0, 2), 16),
    g: Number.parseInt(raw.slice(2, 4), 16),
    b: Number.parseInt(raw.slice(4, 6), 16),
  };
}

/** WCAG 2.x relative luminance for sRGB hex. */
export function relativeLuminance(hex: string): number | null {
  const rgb = parseRgb(hex);
  if (!rgb) return null;
  const r = channelToLinear(rgb.r);
  const g = channelToLinear(rgb.g);
  const b = channelToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Contrast ratio between two sRGB colors (1–21). */
export function contrastRatio(foregroundHex: string, backgroundHex: string): number | null {
  const l1 = relativeLuminance(foregroundHex);
  const l2 = relativeLuminance(backgroundHex);
  if (l1 == null || l2 == null) return null;
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

const WHITE = "#FFFFFF";

/** Contrast of white text on a solid accent background — used for org initials. */
export function contrastWithWhite(accentHex: string): number | null {
  return contrastRatio(WHITE, accentHex);
}

/** AA for normal text on the accent fill (initials at ~12–15px). */
export function meetsWcagAaWithWhite(accentHex: string): boolean {
  const ratio = contrastWithWhite(accentHex);
  return ratio != null && ratio >= 4.5;
}

export function formatContrastRatio(ratio: number): string {
  const rounded = Math.round(ratio * 10) / 10;
  return `${rounded}:1`;
}
